import { graphql } from "gatsby";
import * as React from "react";
import { BlogLinks } from "../components/blog/links";
import { CityLinks } from "../components/destination/city-links";
import { Faq } from "../components/destination/faq";
import { DestinationPageHero } from "../components/destination/hero";
import { Links, LinkType } from "../components/destination/links";
import { DestinationOverviewProps } from "../components/destination/overview";
import { RegionResortCore } from "../components/destination/region-resort-core";
import { Layout } from "../components/layout/layout";
import { ShareContent } from "../components/layout/share";
import { DestinationCards } from "../components/shared/cards";
import { TemperatureUnitContext } from "../context/app-context";
import { useLoc } from "../hooks/useLoc";
import { useMonth } from "../hooks/useMonth";
import { LocationType } from "../interfaces";
import { monthSlugs } from "../services/centralStore";
import firstCapital from "../services/firstCapitalCase";
import textParser from "../services/textParser";
import { Resort, ResortMonth } from "./fragments/resort";
import { Ads } from "../components/shared/ads";

type ResortType = {
    data: Query;
    location: LocationType;
    pageContext: {
        month: string;
        slug: string;
        isSingle: boolean;
        lang?: string;
        fullSlug: string;
        links?: LinkType[];
        topListLinks?: LinkType[];
        year: number;
    };
};

const ResortPage = ({ data, location, pageContext }: ResortType) => {
    //console.log(pageContext, data.resortsJson.writeYearIntoTitle);
    const loc = useLoc(pageContext.lang || "en");
    const [units] = React.useContext(TemperatureUnitContext);
    const { month } = useMonth({});
    const weatherOrClimate =
        month == "climate" ? loc("common.climate") : loc("common.weather");
    const og = `og/res-${pageContext.slug}-weather.png`;
    let header;
    if (pageContext.isSingle) {
        header = `${data.resortsJson.name} ${loc("common.weatherAndClimate")}`;
        if (data.resortsJson.writeYearIntoTitle) {
            header += ` ${loc("common.in")} ${pageContext.year}`;
        }
    } else {
        header = `${data.resortsJson.name} ${weatherOrClimate}`;
        header +=
            month != "climate"
                ? ` ${loc("common.in")} ${loc("common.months." + month)}`
                : "";
        header += " " + pageContext.year;
    }
    const descriptionText = pageContext.isSingle
        ? data.resortsJson.header
        : data.resortsMonthsJson.header;
    const description = textParser({
        text: descriptionText,
        units: units,
        noNbsp: true
    });

    const similarDestinationsText = `${loc(
        "common.otherDestinationsWithSimilar"
    )} ${data.resortsJson.name}`;
    const otherDestinationsText = `${loc("common.otherDestinationsIn")} ${
        data.resortsJson.region.name
    }`;
    const weatherOverviewText = `${loc("common.weatherOverview")} ${loc(
        "common.for"
    )} ${data.resortsJson.name}`;
    const closestCitiesText = `${loc("common.closestCitiesFor")} ${
        data.resortsJson.name
    }`;
    const lastWeekWeatherText = data.resortsJson.currentWeather?.texts?.length
        ? `${loc("common.lastWeeksWeather")} ${loc("common.in")} ${
              data.resortsJson.name
          }`
        : undefined;

    const overviewProperties: DestinationOverviewProps = {
        weatherOverviewText: weatherOverviewText,
        includeFaqs: pageContext.isSingle,
        similarDestinationsText,
        lastWeekWeatherText,
        otherDestinationsText
    };

    let faqs: { answer: string; question: string }[];
    let monthSentences: { answer: string; question: string }[];
    if (!pageContext.isSingle) {
        faqs = data.resortsMonthsJson.questions.map(
            (question: string, idx: number) => {
                return {
                    question: question,
                    answer: data.resortsMonthsJson.answers[idx]
                };
            }
        );
        faqs.splice(0, 0, {
            question:
                firstCapital(weatherOrClimate) + " " + loc("common.overview"),
            answer: data.resortsMonthsJson.header
        });
    } else {
        faqs = data.resortsJson.monthData
            .filter((m) => m.questions.length)
            .map((m) => {
                return { question: m.questions[0], answer: m.answers[0] };
            });
        monthSentences = data.resortsJson.monthData.map((m, idx) => {
            const monthName = loc("common.months." + monthSlugs[idx]);
            return {
                question: `${monthName}  ${loc("common.weather")}`,
                answer: m.header
            };
        });
        monthSentences.splice(0, 0, {
            question: loc("common.weatherOverview"),
            answer: data.resortsJson.header
        });
    }

    return (
        <Layout
            faqs={faqs}
            path={"/" + pageContext.fullSlug}
            og={og}
            title={header}
            description={description}
        >
            <DestinationPageHero
                className="lg:hidden"
                header={header}
                bgImage={
                    data.resortsJson.heroImgFull.childImageSharp.gatsbyImageData
                }
                resort={data.resortsJson}
            />
            <DestinationPageHero
                className="lgmax:hidden"
                header={header}
                bgImage={
                    data.resortsJson.heroImg.childImageSharp.gatsbyImageData
                }
                resort={data.resortsJson}
            />

            <RegionResortCore
                {...{ pageContext, month }}
                locationJson={data.resortsJson}
                overviewProperties={overviewProperties}
                introContent={description}
                introTitle={data.resortsJson.name}
            />

            <Ads idx={2} />
            <div className="h-4"></div>

            <h2 className="w-destination-header font-destination-header m-auto mb-12">
                <a
                    id="destinations-similar"
                    className="relative top-[-100px] md:top-[-120px]"
                />
                {similarDestinationsText}
            </h2>
            <DestinationCards
                regionsOrResorts={
                    pageContext.isSingle
                        ? data.resortsJson.similar
                        : data.resortsMonthsJson.similar
                }
                isResorts={true}
                includeCountry={true}
                className=""
            />

            {data.resortsJson.otherResorts.length && (
                <>
                    <div className="h-16"></div>
                    <h2 className="w-destination-header font-destination-header m-auto mb-12">
                        <a
                            id="destinations-other"
                            className="relative top-[-100px] md:top-[-120px]"
                        />
                        {otherDestinationsText}
                    </h2>
                    <DestinationCards
                        regionsOrResorts={data.resortsJson.otherResorts}
                        isResorts={true}
                        className=""
                    />
                </>
            )}
            {data.resortsJson.closestCities?.length && (
                <CityLinks
                    headerText={closestCitiesText}
                    cities={data.resortsJson.closestCities}
                />
            )}
            <Ads idx={3} />
            <hr className="m-auto mt-16 mb-12 w-full xm:w-3/4" />

            {data.resortsJson.currentWeather?.texts?.length && (
                <>
                    <h2 className="w-destination-header font-destination-header m-auto mt-16 mb-12">
                        <a
                            id="current-weather"
                            className="relative top-[-100px] md:top-[-120px]"
                        />
                        {lastWeekWeatherText}
                    </h2>
                    <Faq
                        adIndex={4}
                        faqs={data.resortsJson.currentWeather.texts.map((t) => {
                            return { answer: t };
                        })}
                    />
                </>
            )}

            {!pageContext.isSingle && (
                <>
                    <h2 className="w-destination-header font-destination-header m-auto mt-16">
                        <a
                            id="weather-overview"
                            className="relative top-[-100px] md:top-[-120px]"
                        />
                        {weatherOverviewText}
                    </h2>
                    <Faq faqs={faqs} adIndex={5} />
                </>
            )}
            {pageContext.isSingle && (
                <>
                    <h2 className="w-destination-header font-destination-header m-auto mt-16">
                        <a
                            id="weather-overview"
                            className="relative top-[-100px] md:top-[-120px]"
                        />
                        {weatherOverviewText}
                    </h2>
                    <Faq faqs={monthSentences} adIndex={5}></Faq>
                    <h2 className="w-destination-header font-destination-header m-auto mt-16">
                        <a
                            id="faqs"
                            className="relative top-[-100px] md:top-[-120px]"
                        />
                        {`${loc("common.faqs")}`}
                    </h2>
                    <Faq faqs={faqs} adIndex={6}></Faq>
                </>
            )}
            <Links links={pageContext.topListLinks} title="Appeared in TOP lists"></Links>
            <Links links={pageContext.links}></Links>
            <BlogLinks continent={data.resortsJson.region.country.continent} />
            <div className="h-24"></div>
            <ShareContent active={true} partOfPage={true} />
        </Layout>
    );
};

interface Query {
    resortsJson: Resort;
    resortsMonthsJson: ResortMonth;
}

export const query = graphql`
    query QueryResort($monthShort: String, $slug: String) {
        resortsJson(slug: { eq: $slug }) {
            ...ResortFull
        }
        resortsMonthsJson(month: { eq: $monthShort }, slug: { eq: $slug }) {
            ...ResortMonth
        }
    }
`;

export default ResortPage;
